import { useEffect, useState } from 'react';
import { History } from 'lucide-react';
import toast from 'react-hot-toast';
import { ApiClient, getErrorMessage } from '@/shared/services/api';
import { FeedItemVersionDto } from '@/shared/types/api';
import { Spinner } from '@/shared/components/Spinner';
import { ExpandableText } from '@/shared/components/ExpandableText';
import { formatDateTime } from '@/shared/utils/date';

interface VersionHistoryPanelProps {
  feedItemId: number;
}

/**
 * Inline history of a feed item's versions (SC-278), newest first. Rendered below the item like
 * the comments/links panels so the reader keeps the context of the current screen.
 */
export function VersionHistoryPanel({ feedItemId }: VersionHistoryPanelProps) {
  const [versions, setVersions] = useState<FeedItemVersionDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ApiClient.getFeedItemVersions(feedItemId)
      .then((result) => {
        if (!cancelled) setVersions(result);
      })
      .catch((error) => {
        if (!cancelled) toast.error(getErrorMessage(error, 'Failed to load version history'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [feedItemId]);

  return (
    <section aria-label="Version history">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        <History className="h-4 w-4 text-primary" />
        Version history
      </h3>

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
          <Spinner />
          Loading versions…
        </div>
      ) : versions.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">No version history for this item.</p>
      ) : (
        <ol className="space-y-2">
          {versions.map((v) => (
            <li key={v.versionNumber} className="rounded-md border border-border bg-surface p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-foreground">
                  v{v.versionNumber} · {v.title}
                </span>
                <time className="text-xs text-muted-foreground" dateTime={v.capturedAt}>
                  {formatDateTime(v.capturedAt)}
                </time>
              </div>
              {v.description && (
                <ExpandableText text={v.description} className="mt-1.5 text-sm text-muted-foreground" />
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
